import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getUsers, createUser, updateUser, deleteUser } from '../../api/userApi';
import { getAllRoles } from '../../api/roleApi';
import { runCreateUsersScript } from '../../api/scriptApi';
import { PlusCircle, Edit, Trash2, X, Save, ChevronDown, ChevronUp, RefreshCw, Shield, Users } from 'lucide-react';
import { getRoleDisplayName, getRoleColorClass } from '../../utils/roleUtils';

// Kullanıcı ekleme/düzenleme modal bileşeni
const UserFormModal = ({ user, onClose, onSubmit, isEditing }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role_id: '', // role_id kullanacağız
    email: '',
    full_name: '',
    is_active: true,
    is_2fa_enabled: false // Default değer, API'ye gönderilecek ama formda gösterilmeyecek
  });
  
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  
  // Rolleri yükle
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await getAllRoles();
        console.log('Roller yüklendi:', response);
        
        // API yanıtı { status, results, data } formatında olabilir veya doğrudan dizi olabilir
        let roleData;
        
        if (Array.isArray(response)) {
          // Doğrudan dizi
          roleData = response;
        } else if (response && response.data && Array.isArray(response.data)) {
          // { status, results, data } formatı
          roleData = response.data;
        } else {
          // Bilinmeyen format, boş dizi kullan
          console.error('Beklenmeyen API yanıt formatı:', response);
          roleData = [];
        }
        
        setRoles(roleData);
      } catch (error) {
        console.error('Roller yüklenirken hata:', error);
        toast.error('Roller yüklenirken hata oluştu');
      } finally {
        setRolesLoading(false);
      }
    };
    
    fetchRoles();
  }, []);
  
  // Düzenleme modunda form verilerini doldur
  useEffect(() => {
    if (isEditing && user) {
      console.log('Düzenlenen kullanıcı:', user);
      
      // 2FA değerini düzgün şekilde dönüştür
      const is2faEnabled = convert2FAValueToBoolean(user.is_2fa_enabled);
      console.log(`Düzenlenen kullanıcının 2FA durumu: ${JSON.stringify(user.is_2fa_enabled)} -> ${is2faEnabled}`);
      
      setFormData({
        username: user.username || '',
        password: '', // Şifre alanını boş bırak (değiştirilmezse mevcut şifre korunur)
        role_id: user.role_id || '',
        email: user.email || '',
        full_name: user.full_name || '',
        is_active: user.is_active === undefined ? true : user.is_active,
        is_2fa_enabled: is2faEnabled
      });
    }
  }, [isEditing, user]);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.username || (!isEditing && !formData.password)) {
      toast.error('Kullanıcı adı ve şifre zorunludur');
      return;
    }
    
    if (!formData.role_id) {
      toast.error('Lütfen bir rol seçin');
      return;
    }
    
    setLoading(true);
    
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      toast.error(`İşlem başarısız: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Rol seçimi alanını güncelleyelim
  const renderRoleSelect = () => {
    if (rolesLoading) {
      return (
        <select
          name="role_id"
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          disabled
        >
          <option>Roller yükleniyor...</option>
        </select>
      );
    }
    
    // Rollerin bir dizi olduğundan emin ol
    const roleOptions = Array.isArray(roles) ? roles : [];
    
    if (roleOptions.length === 0) {
      console.warn('Herhangi bir rol bulunamadı veya roles bir dizi değil:', roles);
    }
    
    return (
      <select
        name="role_id"
        value={formData.role_id}
        onChange={handleChange}
        required
        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
      >
        <option value="">Rol Seçin</option>
        {roleOptions.map(role => (
          <option key={role.id} value={role.id}>{role.display_name}</option>
        ))}
      </select>
    );
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">
            {isEditing ? 'Kullanıcıyı Düzenle' : 'Yeni Kullanıcı Ekle'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4">
            {/* Kullanıcı Adı */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kullanıcı Adı *
              </label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                disabled={isEditing}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
              />
            </div>
            
            {/* Şifre */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {isEditing ? 'Şifre (değiştirmek istiyorsanız)' : 'Şifre *'}
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required={!isEditing}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            {/* Rol */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rol *
              </label>
              {renderRoleSelect()}
            </div>
            
            {/* Tam Ad */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tam Ad
              </label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            {/* E-posta */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                E-posta <span className="text-gray-500 text-xs">(İsteğe bağlı)</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            {/* Aktif/Pasif */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                Hesap Aktif
              </label>
            </div>
            
            {/* 2FA durumu göstergesi - Sadece düzenleme modunda göster */}
            {isEditing && (
              <div className="flex items-center p-2 bg-gray-50 rounded-md">
                <div className="mr-2 text-sm font-medium text-gray-700">2FA Durumu:</div>
                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${formData.is_2fa_enabled ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                  {formData.is_2fa_enabled ? 'Aktif' : 'Pasif'}
                </span>
                <span className="ml-2 text-xs text-gray-500">
                  (Bu ayar kullanıcı tarafından hesap panelinden yönetilir)
                </span>
              </div>
            )}
          </div>
          
          <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 mr-3"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-primary-300 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Kaydediliyor...
                </>
              ) : (
                <>{isEditing ? 'Güncelle' : 'Oluştur'}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Kullanıcı silme onay modalı
const DeleteConfirmModal = ({ user, onClose, onConfirm }) => {
  const [loading, setLoading] = useState(false);
  
  const handleDelete = async () => {
    setLoading(true);
    
    try {
      await onConfirm(user.id);
      onClose();
      toast.success(`${user.username} kullanıcısı silindi`);
    } catch (error) {
      toast.error(`Silme işlemi başarısız: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">Kullanıcıyı Sil</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="p-6">
          <div className="flex items-center text-red-600 mb-4">
            <Shield size={24} className="mr-2" />
            <span className="font-medium">Dikkat</span>
          </div>
          
          <p className="text-gray-700 mb-4">
            <strong>{user?.username}</strong> kullanıcısını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
          </p>
          
          <div className="flex justify-end mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 mr-3"
            >
              İptal
            </button>
            <button
              onClick={handleDelete}
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center"
            >
              {loading ? (
                <>
                  <RefreshCw size={16} className="mr-2 animate-spin" />
                  İşleniyor...
                </>
              ) : (
                <>
                  <Trash2 size={16} className="mr-2" />
                  Sil
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Yardımcı fonksiyon: 2FA değerini Boolean'a dönüştürür
const convert2FAValueToBoolean = (value) => {
  let is2faEnabled = false;
  
  // Tüm olası veri tiplerini kontrol et
  if (value !== undefined && value !== null) {
    if (typeof value === 'number') {
      is2faEnabled = value !== 0;
    } else if (typeof value === 'string') {
      is2faEnabled = value === '1' || 
                    value.toLowerCase() === 'true' || 
                    value === '1.0';
    } else if (typeof value === 'boolean') {
      is2faEnabled = value;
    }
  }
  
  return is2faEnabled;
};

// Ana Kullanıcı Yönetimi bileşeni
const UserManager = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [creatingBulkUsers, setCreatingBulkUsers] = useState(false);
  
  // Modallar
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Sıralama
  const [sortConfig, setSortConfig] = useState({
    key: 'username',
    direction: 'asc'
  });
  
  // Kullanıcıları yükle
  useEffect(() => {
    fetchUsers();
  }, []);
  
  // Kullanıcıları getir
  const fetchUsers = async () => {
    setRefreshing(true);
    
    try {
      // API çağrısını yapmadan önce konsola bilgi yazalım
      console.log('Kullanıcı verileri isteniyor...');
      
      const response = await getUsers();
      console.log('------------------------------');
      console.log('API Yanıtı - Ham Kullanıcı Verileri:', response);
      console.log('------------------------------');
      
      // API yanıtı direkt dizi olabilir veya { status, results, data } formatında olabilir
      let userData;
      
      if (Array.isArray(response)) {
        // Doğrudan dizi
        userData = response;
      } else if (response && response.data && Array.isArray(response.data)) {
        // { status, results, data } formatı
        userData = response.data;
      } else {
        // Bilinmeyen format, boş dizi kullan
        console.error('Beklenmeyen API yanıt formatı:', response);
        userData = [];
      }
      
      // Tüm kullanıcıların ham verilerini göster
      console.log('------------------------------');
      console.log('İşlenecek Kullanıcı Verileri:');
      userData.forEach((user, index) => {
        console.log(`Kullanıcı #${index + 1} - ${user.username}:`);
        console.log('  id:', user.id);
        console.log('  role_id:', user.role_id);
        console.log('  is_active:', user.is_active, typeof user.is_active);
        console.log('  is_2fa_enabled:', user.is_2fa_enabled, typeof user.is_2fa_enabled);
        // Eğer veritabanında is_2fa_enabled varsa ama UI'da gösterilmiyorsa
        // API yanıtında bu alan doğru dönmüyor olabilir
      });
      console.log('------------------------------');
      
      // Alınan verileri işle
      const processedUsers = userData.map(user => {
        // is_2fa_enabled değerinin JSON formatında yazdıralım ki tam değeri görelim
        console.log(`Kullanıcı ${user.username} - 2FA durumu: ${JSON.stringify(user.is_2fa_enabled)}, Türü: ${typeof user.is_2fa_enabled}`);
        
        // 2FA durumunu doğru belirlemek için yardımcı fonksiyonu kullan
        const is2faEnabled = convert2FAValueToBoolean(user.is_2fa_enabled);
        
        console.log(`Kullanıcı ${user.username} - 2FA durumu hesaplandı: ${is2faEnabled}`);
        
        // Eksik alanları tamamla
        return {
          ...user,
          // Geriye uyumluluk - role_name'den role oluştur
          role: user.role_name || (user.role_id ? `role_${user.role_id}` : 'bilinmeyen'),
          // Rol görüntü adını ekle (eğer yoksa)
          role_display_name: user.role_display_name || user.role_name || null,
          // Dönüştürülmüş 2FA değerini kullan
          is_2fa_enabled: is2faEnabled
        };
      });
      
      // Son işlenmiş verileri göster
      console.log('------------------------------');
      console.log('İşlenmiş Kullanıcı Verileri:');
      processedUsers.forEach((user, index) => {
        console.log(`Kullanıcı #${index + 1} - ${user.username}:`);
        console.log('  is_2fa_enabled:', user.is_2fa_enabled, typeof user.is_2fa_enabled);
      });
      console.log('------------------------------');
      
      setUsers(processedUsers);
    } catch (error) {
      console.error('Kullanıcı yükleme hatası:', error);
      toast.error(`Kullanıcılar yüklenirken hata: ${error.message}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  // Sıralama ayarla
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    
    // Kullanıcıları sırala
    const sortedUsers = [...users].sort((a, b) => {
      const aValue = a[key];
      const bValue = b[key];
      
      if (aValue < bValue) {
        return direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
    
    setUsers(sortedUsers);
  };
  
  // Sıralama ikonları
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ChevronDown size={16} className="ml-1 text-gray-400" />;
    }
    
    return sortConfig.direction === 'asc' ? (
      <ChevronUp size={16} className="ml-1 text-primary-500" />
    ) : (
      <ChevronDown size={16} className="ml-1 text-primary-500" />
    );
  };
  
  // Yeni kullanıcı ekle
  const handleAddUser = async (userData) => {
    try {
      console.log('Kullanıcı ekleniyor:', userData);
      if (!userData.role_id) {
        toast.error('Lütfen bir rol seçin');
        throw new Error('Rol seçilmedi');
      }
      
      // 2FA durumunu 0/1 olarak dönüştür
      const apiUserData = {
        ...userData,
        is_2fa_enabled: userData.is_2fa_enabled ? 1 : 0
      };
      
      console.log('API\'ye gönderilen 2FA değeri:', apiUserData.is_2fa_enabled);
      const response = await createUser(apiUserData);
      console.log('Kullanıcı başarıyla eklendi, yanıt:', response);
      
      // API yanıtı doğrudan nesne olabilir veya { data } içerebilir
      let newUserData;
      
      if (response && typeof response === 'object') {
        if (response.id) {
          // Doğrudan kullanıcı nesnesi
          newUserData = response;
        } else if (response.data && response.data.id) {
          // { data: {...} } formatı
          newUserData = response.data;
        } else {
          console.error('API yanıtı beklenen formatta değil:', response);
          toast.error('Sunucudan beklenen veri alınamadı');
          throw new Error('Veri alınamadı');
        }
      } else {
        console.error('API yanıtı beklenen formatta değil:', response);
        toast.error('Sunucudan beklenen veri alınamadı');
        throw new Error('Veri alınamadı');
      }
      
      // API'den dönen 2FA durumunu kontrol et ve dönüştür
      console.log(`API yanıtından gelen 2FA durumu: ${JSON.stringify(newUserData.is_2fa_enabled)}`);
      const is2faEnabled = convert2FAValueToBoolean(newUserData.is_2fa_enabled);
      console.log(`Dönüştürülen 2FA değeri: ${is2faEnabled}`);
      
      // Yanıt verisini işle
      const newUser = {
        ...newUserData,
        // Geriye uyumluluk - role_name'den role oluştur
        role: newUserData.role_name || (newUserData.role_id ? `role_${newUserData.role_id}` : 'bilinmeyen'),
        // Rol görüntü adını ekle (eğer yoksa)
        role_display_name: newUserData.role_display_name || newUserData.role_name || null,
        // Dönüştürülmüş 2FA değerini kullan
        is_2fa_enabled: is2faEnabled
      };
      
      setUsers([...users, newUser]);
      toast.success('Kullanıcı başarıyla eklendi');
      return newUser;
    } catch (error) {
      console.error('Kullanıcı ekleme hatası:', error);
      throw error;
    }
  };
  
  // Kullanıcı güncelle
  const handleUpdateUser = async (userData) => {
    try {
      console.log('Kullanıcı güncelleniyor:', userData);
      if (!userData.role_id) {
        toast.error('Lütfen bir rol seçin');
        throw new Error('Rol seçilmedi');
      }
      
      // 2FA durumunu 0/1 olarak dönüştür
      const apiUserData = {
        ...userData,
        is_2fa_enabled: userData.is_2fa_enabled ? 1 : 0
      };
      
      console.log('API\'ye gönderilen 2FA değeri:', apiUserData.is_2fa_enabled);
      const response = await updateUser(selectedUser.id, apiUserData);
      console.log('Kullanıcı başarıyla güncellendi, yanıt:', response);
      
      // API yanıtı doğrudan nesne olabilir veya { data } içerebilir
      let updatedUserData;
      
      if (response && typeof response === 'object') {
        if (response.id) {
          // Doğrudan kullanıcı nesnesi
          updatedUserData = response;
        } else if (response.data && response.data.id) {
          // { data: {...} } formatı
          updatedUserData = response.data;
        } else {
          console.error('API yanıtı beklenen formatta değil:', response);
          toast.error('Sunucudan beklenen veri alınamadı');
          throw new Error('Veri alınamadı');
        }
      } else {
        console.error('API yanıtı beklenen formatta değil:', response);
        toast.error('Sunucudan beklenen veri alınamadı');
        throw new Error('Veri alınamadı');
      }
      
      // API'den dönen 2FA durumunu kontrol et ve dönüştür
      console.log(`API yanıtından gelen 2FA durumu: ${JSON.stringify(updatedUserData.is_2fa_enabled)}`);
      const is2faEnabled = convert2FAValueToBoolean(updatedUserData.is_2fa_enabled);
      console.log(`Dönüştürülen 2FA değeri: ${is2faEnabled}`);
      
      // Yanıt verisini işle
      const updatedUser = {
        ...updatedUserData,
        // Geriye uyumluluk - role_name'den role oluştur
        role: updatedUserData.role_name || (updatedUserData.role_id ? `role_${updatedUserData.role_id}` : 'bilinmeyen'),
        // Rol görüntü adını ekle (eğer yoksa)
        role_display_name: updatedUserData.role_display_name || updatedUserData.role_name || null,
        // Dönüştürülmüş 2FA değerini kullan
        is_2fa_enabled: is2faEnabled
      };
      
      setUsers(users.map(user => user.id === selectedUser.id ? updatedUser : user));
      toast.success('Kullanıcı başarıyla güncellendi');
      return updatedUser;
    } catch (error) {
      console.error('Kullanıcı güncelleme hatası:', error);
      throw error;
    }
  };
  
  // Kullanıcı sil
  const handleDeleteUser = async (id) => {
    try {
      await deleteUser(id);
      setUsers(users.filter(user => user.id !== id));
      return true;
    } catch (error) {
      console.error('Kullanıcı silme hatası:', error);
      throw error;
    }
  };
  
  // Toplu kullanıcı oluşturma
  const handleCreateBulkUsers = async () => {
    if (creatingBulkUsers) return;

    const confirmed = window.confirm(
      "Bu işlem, server/scripts/kullanıcılar.json dosyasından kullanıcı hesaplarını otomatik olarak oluşturacaktır. Devam etmek istiyor musunuz?"
    );

    if (!confirmed) return;

    setCreatingBulkUsers(true);
    
    try {
      const response = await runCreateUsersScript();
      if (response.success) {
        toast.success(response.message);
        // Kullanıcı listesini yenile
        fetchUsers();
      } else {
        toast.error(`Hata: ${response.message}`);
      }
    } catch (error) {
      console.error('Toplu kullanıcı oluşturma hatası:', error);
      toast.error(`İşlem sırasında bir hata oluştu: ${error.message || 'Bilinmeyen hata'}`);
    } finally {
      setCreatingBulkUsers(false);
    }
  };
  
  // Yükleme durumu
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-800">Kullanıcı Yönetimi</h2>
        <div className="flex space-x-2">
          <button
            onClick={fetchUsers}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 flex items-center"
            disabled={refreshing}
          >
            <RefreshCw size={16} className={`mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            Yenile
          </button>
          <button
            onClick={handleCreateBulkUsers}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 flex items-center"
            disabled={creatingBulkUsers}
          >
            <Users size={16} className={`mr-1 ${creatingBulkUsers ? 'animate-pulse' : ''}`} />
            {creatingBulkUsers ? 'İşleniyor...' : 'Toplu Kullanıcı Ekle (.json)'}
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 flex items-center"
          >
            <PlusCircle size={16} className="mr-1" />
            Yeni Kullanıcı
          </button>
        </div>
      </div>
      
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('username')}
                >
                  <div className="flex items-center">
                    Kullanıcı Adı {getSortIcon('username')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('role_id')}
                >
                  <div className="flex items-center">
                    Rol {getSortIcon('role_id')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('full_name')}
                >
                  <div className="flex items-center">
                    Tam Ad {getSortIcon('full_name')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('email')}
                >
                  <div className="flex items-center">
                    E-posta {getSortIcon('email')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Durum
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('is_2fa_enabled')}
                >
                  <div className="flex items-center">
                    2FA {getSortIcon('is_2fa_enabled')}
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.length > 0 ? (
                users.map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{user.username}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleColorClass(user.role_id)}`}>
                        {user.role_name || user.role_display_name || getRoleDisplayName(user.role_id) || `Rol #${user.role_id}`}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.full_name || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{user.email || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {user.is_active ? 'Aktif' : 'Pasif'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.is_2fa_enabled ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                        {user.is_2fa_enabled ? 'Aktif' : 'Pasif'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      <button
                        onClick={() => {
                          setSelectedUser(user);
                          setShowEditModal(true);
                        }}
                        className="text-primary-600 hover:text-primary-900 p-1"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedUser(user);
                          setShowDeleteModal(true);
                        }}
                        className="text-red-600 hover:text-red-900 p-1"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-10 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        className="h-10 w-10 text-gray-300 mb-3" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      <p className="text-lg font-medium">Henüz kullanıcı bulunmuyor</p>
                      <p className="text-sm mt-1 mb-3">
                        Yeni kullanıcılar ekleyerek başlayabilirsiniz
                      </p>
                      <button
                        onClick={() => setShowAddModal(true)}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center"
                      >
                        <PlusCircle size={16} className="mr-1" />
                        Yeni Kullanıcı Ekle
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Modallar */}
      {showAddModal && (
        <UserFormModal
          user={null}
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddUser}
          isEditing={false}
        />
      )}
      
      {showEditModal && (
        <UserFormModal
          user={selectedUser}
          onClose={() => setShowEditModal(false)}
          onSubmit={handleUpdateUser}
          isEditing={true}
        />
      )}
      
      {showDeleteModal && (
        <DeleteConfirmModal
          user={selectedUser}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteUser}
        />
      )}
    </div>
  );
};

export default UserManager;
